import { restClient } from '@/Core/Services/RestClientService'
import { resourceClient } from '@/Core/Services/ResourceClientService'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { DeepCloneTask } from '@/Core/Tasks/Utils/DeepCloneTask'
import { Form, FormField } from '@/Core/UI/Form'
import { HeaderPage } from '@/Core/UI/HeaderPage'
import { ButtonGroup } from '@/Core/UI/ButtonGroup'
import { App, Col, Row, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { historyStackManager } from '../../Managers/HistoryStackManager'
import { useNavigationContext } from '../../Providers/NavigationProvider'
import { Drawer } from '../Drawer/Drawer'
import { Modal } from '../Modal/Modal'
import { MercureHandler } from './Components/MercureHandler'
import {
    createCrudResourceContext,
    CrudResourceContextProvider,
    useCrudResourceContext,
} from './CrudResourceContextProvider'
import { GetRestUrlTask } from '@/Core/Tasks/Resources/GetRestUrlTask'

/**
 * Шаблон завантаження для форми
 */
const loadingTemplate = (
    <Row gutter={[24, 24]}>
        <Col span={12}>
            <Skeleton active={true} />
            <Skeleton active={true} />
            <Skeleton active={true} />
            <Skeleton active={true} />
        </Col>
        <Col span={12}>
            <Skeleton active={true} />
            <Skeleton active={true} />
            <Skeleton active={true} />
            <Skeleton active={true} />
        </Col>
    </Row>
)

/**
 * Обробник помилок валідації та інших серверних помилок (винесений за межі компонента)
 * Приймає notification та t як параметри в момент виклику, а не створення
 */
export const handleValidationError = (response, formContext, notification, t) => {
    if (response && Array.isArray(response.violations)) {
        let errors = {}

        response.violations.forEach((violation) => {
            // Підтримуємо обидва формати: propertyPath (старий) та field (новий)
            const fieldName = violation.field || violation.propertyPath
            const errorMessage = violation.message ?? violation.title
            
            if (fieldName) {
                errors[fieldName] = errorMessage
            }
        })

        formContext.setErrors(errors)

        // Формуємо опис помилок з лейблів полів
        const violationDescription = response.violations
            .map((violation) => {
                const fieldName = violation.field || violation.propertyPath
                const label = fieldName
                    ? (formContext.fields?.get(fieldName)?.label ?? fieldName)
                    : null
                const prefix = label ? `${label}: ` : ''
                return `${prefix}${violation.message ?? violation.title}`
            })
            .join('\n')

        // Викликаємо notification через setTimeout щоб уникнути виклику в render
        setTimeout(() => {
            notification.error({
                duration: 3,
                message: t('Data entry error.', { ns: 'Core' }),
                description: violationDescription || t('Please correct the errors in the form.', { ns: 'Core' }),
                placement: 'bottomRight',
            })
        }, 0)
    } else if (response && response.message) {
        // Новий формат помилок з message
        setTimeout(() => {
            notification.error({
                duration: 3,
                message: t('Error', { ns: 'Core' }),
                description: response.message,
                placement: 'bottomRight',
            })
        }, 0)
    } else if (response && response['hydra:description'] !== undefined) {
        // Старий формат помилок з hydra:description
        setTimeout(() => {
            notification.error({
                duration: 3,
                message: t('Error', { ns: 'Core' }),
                description: response['hydra:description'],
                placement: 'bottomRight',
            })
        }, 0)
    }
}

/**
 * ResourceDetail - компонент для відображення деталей ресурсу
 *
 * Підтримує режими:
 * - view: перегляд існуючого елементу (потребує itemId)
 * - edit: редагування існуючого елементу (потребує itemId)
 * - create: створення нового елементу
 *
 * При режимах view/edit автоматично завантажує дані через resourceClient.get()
 *
 * Підтримує відображення в 3х режимах:
 * - page: звичайна сторінка
 * - drawer: бічна панель
 * - modal: модальне вікно
 *
 * @param {Object} props
 * @param {Object} props.crudContext - контекст CRUD ресурсу (опціонально, створюється автоматично)
 * @param {string} props.registryKey - ОБОВ'ЯЗКОВИЙ ключ реєстру (якщо не передано crudContext)
 *   Приклад: "Setting.User.card.edit.123"
 *   Для генерації використовуйте: generateRegistryKey.card('Setting.User', 'edit', 123)
 * @param {Object} props.listCrudContext - контекст списку ресурсу (для перезавантаження списку)
 * @param {string|number} props.itemId - ID елементу для view/edit режимів
 * @param {'view'|'edit'|'create'} props.mode - режим відображення
 * @param {'page'|'drawer'|'modal'} props.displayMode - режим відображення wrapper (опціонально)
 * @param {Function} props.onClose - callback при закритті drawer/modal
 * @param {Function} props.onSave - callback при збереженні
 * @param {Function} props.onModeChange - callback при зміні режиму
 * @param {boolean} props.disableAutoRedirectAfterCreate - вимкнути автоматичне перенаправлення на режим редагування після створення
 * 
 * ВАЖЛИВО: параметр useRegistry видалено - всі контексти зберігаються в registry
 */
export const ResourceDetail = ({
    crudContext,
    registryKey,
    listCrudContext,
    itemId,
    mode,
    displayMode: propDisplayMode,
    onClose,
    onSave,
    onModeChange,
    disableAutoRedirectAfterCreate = false,
    ...crudProps
}) => {
    const { notification } = App.useApp()
    const { t } = useTranslation()
    const parentCrudContext = useCrudResourceContext()
    const { isProgrammaticNavigation } = useNavigationContext()

    /**
     * Визначає displayMode на основі рівня стеку
     */
    const determineDisplayModeByLevel = (level) => {
        if (level <= 0) return 'page'
        if (level === 1) return 'drawer'
        return 'modal'
    }

    /**
     * Визначає режим відображення з урахуванням mode та конфігурації ресурсу
     * @param {string} currentMode - поточний режим (view/edit/create)
     * @param {Object} resource - конфігурація ресурсу
     * @param {string} explicitDisplayMode - явно переданий displayMode
     * @returns {string} displayMode (page/drawer/modal)
     */
    const determineDisplayMode = (currentMode, resource, explicitDisplayMode) => {
        
        // 1. Пріоритет - явно переданий displayMode
        if (explicitDisplayMode) {
            return explicitDisplayMode
        }
        
        // 2. Якщо це прямий перехід (не програмна навігація) - завжди page
        if (typeof window !== 'undefined' && !isProgrammaticNavigation()) {
            return 'page'
        }
        
        // 3. Конфігурація для конкретного mode
        if (resource?.detailConfig?.modeConfig?.[currentMode]?.displayMode) {
            return resource.detailConfig.modeConfig[currentMode].displayMode
        }
        
        // 4. Загальна конфігурація detailConfig.mode
        if (resource?.detailConfig?.mode) {
            return resource.detailConfig.mode
        }
        
        // 5. Автоматично по рівню стека (якщо програмна навігація)
        if (typeof window !== 'undefined' && isProgrammaticNavigation()) {
            const currentLevel = historyStackManager.getCurrentLevel()
            return determineDisplayModeByLevel(currentLevel + 1)
        }
        
        // 6. За замовчуванням
        return 'drawer'
    }

    /**
     * Визначає template з урахуванням mode та конфігурації ресурсу
     * @param {string} currentMode - поточний режим (view/edit/create)
     * @param {Object} resource - конфігурація ресурсу
     * @returns {Component|null} template компонент або null
     */
    const determineTemplate = (currentMode, resource) => {
        // 1. Пріоритет - template для конкретного mode
        if (resource?.detailConfig?.modeConfig?.[currentMode]?.template) {
            return resource.detailConfig.modeConfig[currentMode].template
        }
        
        // 2. Загальний template ресурсу
        if (resource?.template) {
            return resource.template
        }
        
        // 3. Template форми
        if (resource?.form?.template) {
            return resource.form.template
        }
        
        // 4. За замовчуванням - null (буде згенерована дефолтна форма)
        return null
    }

    /**
     * Визначає початковий режим відображення (page/drawer/modal)
     * Викликається ОДРАЗУ при ініціалізації, до першого рендеру
     */
    const determineInitialDisplayMode = () => {
        return determineDisplayMode(mode, crudProps.resource, propDisplayMode)
    }

    // Визначаємо displayMode ОДРАЗУ при ініціалізації
    const initialDisplayMode = determineInitialDisplayMode()
    const [displayMode, setDisplayMode] = useState(initialDisplayMode)
    const [currentContext, setCurrentContext] = useState(null)
    
    /**
     * Очищення контексту при розмонтуванні (тільки для drawer/modal)
     */
    useEffect(() => {
        return () => {
            // Очищаємо контекст тільки для drawer/modal, page контексти залишаємо
            if (crudContext?.resource?.key && (displayMode === 'drawer' || displayMode === 'modal')) {
                crudResourceRegistry.clearDetailContext({
                    resourceName: crudContext.resource.key,
                    mode,
                    itemId: mode === 'create' ? undefined : itemId,
                })
            }
        }
    }, [crudContext?.resource?.key, itemId, mode, displayMode])
    
    /**
     * Оновлює displayMode якщо propDisplayMode змінюється
     */
    useEffect(() => {
        if (propDisplayMode && propDisplayMode !== displayMode) {
            setDisplayMode(propDisplayMode)
        }
    }, [propDisplayMode])
    
    /**
     * Оновлює displayMode в crudContext при його зміні
     * Це важливо для action handlers (view/edit), щоб вони знали поточний режим відображення
     */
    useEffect(() => {
        if (crudContext) {
            crudContext.currentDetailMode = displayMode
        }
    }, [displayMode, crudContext])
    
    /**
     * Зберігаємо контекст списку в detail контексті
     */
    useEffect(() => {
        console.log('[ResourceDetail] listCrudContext effect:', {
            hasListCrudContext: !!listCrudContext,
            hasCrudContext: !!crudContext,
            listContextActions: listCrudContext?.listContext?.actions ? Object.keys(listCrudContext.listContext.actions) : null,
            hasLoadFn: typeof listCrudContext?.listContext?.actions?.load === 'function',
        })
        if (listCrudContext && crudContext) {
            crudContext.listCrudContext = listCrudContext
        }
    }, [listCrudContext, crudContext])
    
    /**
     * Створює контекст для stack manager
     */
    useEffect(() => {
        if (crudContext?.resource?.key) {
            const stackContext = {
                level: historyStackManager.getCurrentLevel() + 1,
                displayMode: displayMode,
                resourceName: crudContext.resource.key,
                resourceId: itemId,
                mode: mode || 'view',
                timestamp: Date.now(),
            }
            setCurrentContext(stackContext)
        }
    }, [crudContext?.resource?.key, itemId, mode, displayMode])

    /**
     * Визначаємо disableAutoRedirectAfterCreate з конфігурації ресурсу або з props
     * За замовчуванням TRUE (нова поведінка) - закриваємо карточку без редиректу
     * Для старої поведінки (авто-редагування) встановіть false
     */
    const shouldDisableAutoRedirect = useMemo(() => {
        // Пріоритет: props > modeConfig.create > resource
        if (disableAutoRedirectAfterCreate !== undefined && disableAutoRedirectAfterCreate !== false) {
            return disableAutoRedirectAfterCreate
        }
        
        return crudProps.resource?.detailConfig?.modeConfig?.create?.disableAutoRedirectAfterCreate ?? 
               crudProps.resource?.disableAutoRedirectAfterCreate ??
               true  // Змінено з false на true - нова поведінка за замовчуванням
    }, [disableAutoRedirectAfterCreate, crudProps.resource])

    /**
     * Визначаємо showSuccessNotificationAfterCreate з конфігурації ресурсу
     * Контролює чи показувати повідомлення про успішне створення
     */
    const shouldShowSuccessNotification = useMemo(() => {
        return crudProps.resource?.detailConfig?.modeConfig?.create?.showSuccessNotificationAfterCreate ?? true
    }, [crudProps.resource])

    /**
     * Підготовка обробника відправки форми для включення в crudProps
     */
    const prepareFormSubmitHandler = useCallback(() => {
        return {
            crudResourceFormSubmit: async ({ values, formContext }) => {
                
                formContext.setIsLoading(true)

                formContext.eventManager.emit('onBeforeTransformSendSaveRequest', { values, formContext })
                
                Object.keys(values).map((key) => {
                    let value = values[key]

                    if (Array.isArray(value)) {
                        value = value.filter(
                            (arrayValue) => arrayValue !== null && arrayValue !== undefined && arrayValue !== ''
                        )
                    }

                    let field = formContext.fields.get(key)
                    
                    if (!field) {
                        if (typeof value === 'object' && value !== null && value['@id'] !== undefined)
                            values[key] = value['@id']

                        return
                    }

                    // TODO: !!!
                    if (Array.isArray(value) && field.isMultiple == false && field.inputProps?.mode !== 'multiple') {
                        value = value[0]
                    }

                    if (value === undefined) value = null

                    if (!Array.isArray(value) && dayjs.isDayjs(value)) value = value.format()

                    if (Array.isArray(value))
                        value = value.map((v) => {
                            if (dayjs.isDayjs(v)) return v.format()
                            if (typeof v === 'object' && typeof field.props?.responseValue === 'function')
                                return field.props.responseValue(v)
                            else if (typeof v === 'object' && v['@id'] !== undefined) return v['@id']

                            return v
                        })

                    // TODO: !!!
                    if (typeof value === 'object' && value !== null && typeof field.props?.responseValue === 'function')
                        value = field.props.responseValue(value)
                    else if (typeof value === 'object' && value !== null && value['@id'] !== undefined)
                        value = value['@id']

                    if (field.type === 'file' && !value) value = []

                    // Застосовуємо функцію output якщо вона визначена в полі
                    if (typeof field.output === 'function') {
                        value = field.output(value)
                    }
                    
                    if (field.type === 'file' && Array.isArray(value) && value?.[0]) 
                        value = value[0]
                    else if (field.type === 'file' && Array.isArray(value) && !value?.[0])
                        value = null
                    
                    values[key] = value
                })
                
                delete values.id
                delete values['@id']

                const result = await formContext.eventManager.emitAsync('onBeforeSendSaveRequest', {
                    values,
                    formContext,
                })
                
                // Need to stop
                if (Array.isArray(result) && result.filter((el) => el === false).length > 0) {
                    formContext.setIsLoading(false)
                    return
                }

                const primaryFieldCode = crudContext?.resource?.primaryFieldCode || 'uuid'
                
                if (formContext.values?.['@id']) {
                    // Формуємо початковий URL для PATCH запиту
                    let patchUrl = GetRestUrlTask(crudContext.resource.restUrl, 'patch', { id: formContext.values?.[primaryFieldCode] }) + '?permissions=true'

                    // Викликаємо подію для модифікації URL перед відправкою запиту на редагування
                    const patchUrlResult = await formContext.eventManager.emitAsync('onBeforeUpdateRequestUrl', {
                        url: patchUrl,
                        values,
                        formContext,
                    })

                    // Якщо подія повернула новий URL - використовуємо його
                    if (Array.isArray(patchUrlResult) && patchUrlResult.length > 0) {
                        const modifiedUrl = patchUrlResult.find(item => typeof item.result === 'string')
                        if (modifiedUrl) {
                            patchUrl = modifiedUrl.result
                        }
                    }

                    restClient
                        .patch(
                            patchUrl,
                            {
                                json: values,
                                headers: {
                                    'X-LOCALE': formContext.currentLanguage,
                                },
                            }
                        )
                        .then((response) => {
                            
                            if ((response?.title && response.title === 'Validation Failed') || response?.code === 422) {
                                handleValidationError(response, formContext, notification, t)
                                formContext.setIsLoading(false)
                                return
                            }

                            notification.success({
                                duration: 3,
                                message: t('Save result', { ns: 'Core' }),
                                description: t('Data successfully saved', { ns: 'Core' }),
                                placement: 'bottomRight',
                            })

                            formContext.eventManager.emit('onAfterSendSaveRequest', {
                                response,
                                values,
                                formContext,
                                crudContext,
                            })

                            formContext.setInitialValues(response)

                            // Перезавантажуємо список через listCrudContext (пряме посилання)
                            console.log('[ResourceDetail][PATCH] After save - checking reload:', {
                                hasListCrudContext: !!crudContext.listCrudContext,
                                hasListContext: !!crudContext.listCrudContext?.listContext,
                                hasActions: !!crudContext.listCrudContext?.listContext?.actions,
                                hasLoadFn: typeof crudContext.listCrudContext?.listContext?.actions?.load === 'function',
                                resourceKey: crudContext.resource?.key,
                            })
                            if (crudContext.listCrudContext?.listContext?.actions?.load) {
                                // Виклик load на listContext (правильна структура для списку)
                                console.log('[ResourceDetail][PATCH] Calling listContext.actions.load(true)')
                                crudContext.listCrudContext.listContext.actions.load(true)
                            } else if (crudContext.resource?.key) {
                                // Fallback: перезавантажуємо список через registry (тихо, без попереджень)
                                console.log('[ResourceDetail][PATCH] Fallback: reloadListIfExists via registry, key:', crudContext.resource.key)
                                if (crudContext.isLiveCrud) {
                                    MercureHandler({
                                        crudContext,
                                        response,
                                        loadData: () => crudResourceRegistry.reloadListIfExists(crudContext.resource.key),
                                    })
                                } else {
                                    crudResourceRegistry.reloadListIfExists(crudContext.resource.key)
                                }
                            } else {
                                console.warn('⚠️ Cannot reload list - no valid reload method found')
                            }

                            formContext.setIsLoading(false)

                            formContext.eventManager.emit('onAfterSuccessSubmit', { formContext, crudContext })
                        })
                        .catch((error) => {
                            // RestClientService вже розпарсив response, тому error.response - це JSON об'єкт
                            const response = error?.response

                            if (response) {
                                handleValidationError(response, formContext, notification, t)
                            } else if (error?.message) {
                                // Якщо немає response, показуємо загальне повідомлення
                                setTimeout(() => {
                                    notification.error({
                                        duration: 3,
                                        message: t('Error', { ns: 'Core' }),
                                        description: error.message,
                                        placement: 'bottomRight',
                                    })
                                }, 0)
                            }

                            formContext.setIsLoading(false)
                        })
                } else {
                    // Формуємо початковий URL для POST запиту
                    let postUrl = GetRestUrlTask(crudContext.resource.restUrl, 'post') + '?permissions=true'

                    // Викликаємо подію для модифікації URL перед відправкою запиту на створення
                    const postUrlResult = await formContext.eventManager.emitAsync('onBeforeCreateRequestUrl', {
                        url: postUrl,
                        values,
                        formContext,
                    })
                    
                    // Якщо подія повернула новий URL - використовуємо його
                    if (Array.isArray(postUrlResult) && postUrlResult.length > 0) {
                        const modifiedUrl = postUrlResult.find(item => typeof item.result === 'string')
                        if (modifiedUrl) {
                            postUrl = modifiedUrl.result
                        }
                    }

                    formContext.eventManager.emit('onBeforeCreateRequestSend', { values, formContext })

                    restClient
                        .post(
                            postUrl,
                            {
                                json: values,
                                headers: {
                                    'X-LOCALE': formContext.currentLanguage,
                                },
                            }
                        )
                        .then((response) => {
                            if ((response?.title && response.title === 'Validation Failed') || response?.code === 422) {
                                handleValidationError(response, formContext, notification, t)
                                formContext.setIsLoading(false)
                                return
                            }

                            formContext.eventManager.emit('onAfterSendSaveRequest', { response, values, formContext })
                            formContext.setInitialValues(response)

                            // Перезавантажуємо список через listCrudContext (пряме посилання)
                            console.log('[ResourceDetail][POST] After save - checking reload:', {
                                hasListCrudContext: !!crudContext.listCrudContext,
                                hasListContext: !!crudContext.listCrudContext?.listContext,
                                hasActions: !!crudContext.listCrudContext?.listContext?.actions,
                                hasLoadFn: typeof crudContext.listCrudContext?.listContext?.actions?.load === 'function',
                                resourceKey: crudContext.resource?.key,
                            })
                            if (crudContext.listCrudContext?.listContext?.actions?.load) {
                                // Виклик load на listContext (правильна структура для списку)
                                console.log('[ResourceDetail][POST] Calling listContext.actions.load(true)')
                                crudContext.listCrudContext.listContext.actions.load(true)
                            } else if (crudContext.resource?.key) {
                                // Fallback: перезавантажуємо список через registry (тихо, без попереджень)
                                console.log('[ResourceDetail][POST] Fallback: reloadListIfExists via registry, key:', crudContext.resource.key)
                                if (crudContext.isLiveCrud) {
                                    MercureHandler({
                                        crudContext,
                                        response,
                                        loadData: () => crudResourceRegistry.reloadListIfExists(crudContext.resource.key),
                                    })
                                } else {
                                    crudResourceRegistry.reloadListIfExists(crudContext.resource.key)
                                }
                            } else {
                                console.warn('⚠️ Cannot reload list - no valid reload method found')
                            }

                            // Показуємо повідомлення про успішне створення (якщо не відключено в налаштуваннях)
                            if (shouldShowSuccessNotification) {
                                notification.success({
                                    duration: 3,
                                    message: t('Save result', { ns: 'Core' }),
                                    description: t('Data successfully saved', { ns: 'Core' }),
                                    placement: 'bottomRight',
                                })
                            }

                            formContext.setIsLoading(false)
                            formContext.eventManager.emit('onAfterSuccessSubmit', { formContext, crudContext })
                            
                            // Нова поведінка (за замовчуванням): закриваємо карточку створення
                            // Стара поведінка (авто-редагування): встановіть disableAutoRedirectAfterCreate = false
                            if (shouldDisableAutoRedirect) {
                                // Закриваємо карточку через closeResourceDetail callback
                                if (crudContext.closeResourceDetail) {
                                    crudContext.closeResourceDetail()
                                }
                            } else {
                                // Стара поведінка: автоматично відкриваємо режим редагування
                                if (crudContext?.actions?.edit && response) {
                                    crudContext.actions.edit(response)
                                }
                            }
                        })
                        .catch((error) => {
                            // RestClientService вже розпарсив response, тому error.response - це JSON об'єкт
                            const response = error?.response

                            if (response) {
                                handleValidationError(response, formContext, notification, t)
                            } else if (error?.message) {
                                // Якщо немає response, показуємо загальне повідомлення
                                setTimeout(() => {
                                    notification.error({
                                        duration: 3,
                                        message: t('Error', { ns: 'Core' }),
                                        description: error.message,
                                        placement: 'bottomRight',
                                    })
                                }, 0)
                            }

                            formContext.setIsLoading(false)
                        })
                }
            },
        }
    }, [crudContext, notification, t, shouldDisableAutoRedirect, shouldShowSuccessNotification])
    
    // Клонуємо crudProps для уникнення мутації
    const preparedProps = useMemo(() => {
        const props = { ...crudProps }

        // Підготовка form конфігурації
        if (!props.form) props.form = {}
        if (!props.form.eventHandlers) props.form.eventHandlers = {}
        if (!props.form.eventHandlers.onSubmit) props.form.eventHandlers.onSubmit = {}

        // Додаємо наш обробник до існуючих
        props.form.eventHandlers.onSubmit = {
            ...props.form.eventHandlers.onSubmit,
            ...prepareFormSubmitHandler(),
        }

        return props
    }, [crudProps, prepareFormSubmitHandler])

    // Якщо не передано готовий контекст - створюємо новий з ОБОВ'ЯЗКОВИМ registryKey
    if (!crudContext) {
        // Валідація: registryKey обов'язковий для створення нового контексту
        if (!registryKey) {
            throw new Error(
                '[ResourceDetail] Параметр "registryKey" є обов\'язковим якщо не передано готовий crudContext.\n' +
                'Приклад: registryKey="Setting.User.card.edit.123"\n' +
                'Для генерації використовуйте: generateRegistryKey.card("Setting.User", "edit", 123) з @/Core/Utils/generateRegistryKey'
            )
        }

        crudContext = createCrudResourceContext({
            contextType: 'detail',
            mode,
            itemId,
            registryKey, // Передаємо обов'язковий ключ
            ...preparedProps,
        })
    }
    else
        crudContext.mergeProps({
            contextType: 'detail',
            mode,
            itemId,
            ...preparedProps,
        })

    if (parentCrudContext) crudContext.setParentCrudContext(parentCrudContext)

    // Сохраняем текущий displayMode в crudContext для использования в action handlers
    // Это позволяет ViewActionHandlerTask и EditActionHandlerTask знать, в каком режиме открыта карточка
    if (crudContext) {
        crudContext.currentDetailMode = displayMode
    }

    /**
     * Завантаження даних ресурсу з API
     */
    const loadResourceData = async () => {
        if (!crudContext || !itemId || !crudContext.resource) return

        try {
            // Встановлюємо стан завантаження
            if (crudContext.formContext) {
                crudContext.formContext.reset()
                crudContext.formContext.setIsLoading(true)
                crudContext.formContext.setIsEditable(mode === 'edit')
            }

            // Виконуємо запит до API завжди без кешу (forceRefresh)
            const initialValues = await restClient.get(
                GetRestUrlTask(crudContext.resource.restUrl, 'get', { id: itemId }) + '?permissions=true',
                {
                    headers: {
                        'X-LOCALE': crudContext.formContext?.currentLanguage || 'uk_UA',
                    },
                },
                { forceRefresh: true },
            )
            // Викликаємо події
            if (crudContext.eventManager) {
                crudContext.eventManager.emit('onResponseRequest', {
                    initialValues,
                    crudContext,
                })

                if (mode === 'view') {
                    crudContext.eventManager.emit('onResponseViewRequest', {
                        initialValues,
                        crudContext,
                    })
                } else if (mode === 'edit') {
                    crudContext.eventManager.emit('onResponseEditRequest', {
                        initialValues,
                        crudContext,
                    })
                }
            }

            // Встановлюємо завантажені дані в форму
            if (crudContext.formContext) {
                crudContext.formContext.setInitialValues(initialValues)
                crudContext.formContext.setStorage('responseData', { ...initialValues })
                crudContext.formContext.setIsLoading(false)
                crudContext.accessItem = initialValues
            }
        } catch (error) {
            console.error('❌ Failed to load resource data:', error)
            console.error('Error details:', {
                itemId,
                mode,
                resource: crudContext.resource?.key || 'unknown',
                errorMessage: error?.message,
                errorName: error?.name,
                errorResponse: error?.response,
                errorStack: error?.stack,
            })
            
            // Перевіряємо чи це помилка читання body
            if (error?.message?.includes('body') || error?.message?.includes('already been read')) {
                console.error('🚨 BODY ALREADY READ ERROR - This is the json() duplication bug!')
            }

            // 401 — токен протік. BaseHttpClient автоматично робить refresh + retry.
            // Якщо сюди потрапили — значить retry теж не вдався (або race condition
            // між паралельними 401-запитами). Не показуємо "Сутність не знайдена",
            // бо це оманлива помилка: проблема в авторизації, а не у відсутності сутності.
            if (error?.statusCode === 401) {
                return
            }

            setTimeout(() => {
                notification.error({
                    duration: 3,
                    message: t('Not found.', { ns: 'Core' }),
                    description: t('The selected entity does not exist.', { ns: 'Core' }),
                    placement: 'bottomRight',
                })
            }, 0)
        } finally {
            // Вимикаємо стан завантаження
            if (crudContext.formContext) {
                crudContext.formContext.setIsLoading(false)
            }
        }
    }

    /**
     * Налаштування режиму створення нового елементу
     */
    const setupCreateMode = () => {
        if (!crudContext) return

        if (crudContext.formContext) {
            crudContext.formContext.reset()
            crudContext.formContext.setIsEditable(true)
            crudContext.formContext.setIsLoading(false)

            // Встановлюємо початкові значення з різних джерел
            // Пріоритет: crudProps.initialValues > crudProps.form?.initialValues
            let initialValues = {}

            if (crudProps.form?.initialValues) {
                initialValues = { ...initialValues, ...crudProps.form.initialValues }
            }

            if (crudProps.initialValues) {
                initialValues = { ...initialValues, ...crudProps.initialValues }
            }

            if (Object.keys(initialValues).length > 0) {
                crudContext.formContext.setInitialValues(initialValues)
            }
        }

        // Викликаємо подію створення
        if (crudContext.eventManager) {
            crudContext.eventManager.emit('onCreateMode', {
                crudContext,
            })
        }
    }

    /**
     * Завантаження даних ресурсу при режимах view або edit
     * ВАЖЛИВО: Реагує на зміни itemId та mode для перезавантаження при переключенні режимів
     */
    useEffect(() => {
        if (!crudContext) return

        // Якщо є itemId та режим view або edit - завантажуємо дані
        if (itemId && (mode === 'view' || mode === 'edit')) {
            loadResourceData()
        } else if (mode === 'create') {
            // Для режиму create встановлюємо початкові значення
            setupCreateMode()
        }
    }, [itemId, mode, crudContext])

    /**
     * Додатковий useEffect для відстеження зміни режиму
     * Необхідний для перезавантаження даних при переключенні view ↔ edit
     * особливо коли використовується replace в OpenResourceCardTask
     */
    useEffect(() => {
        if (!crudContext || !itemId) return
        
        // Перевіряємо чи змінився режим та чи є formContext
        if (crudContext.formContext && (mode === 'view' || mode === 'edit')) {
            // Оновлюємо isEditable відповідно до режиму
            const shouldBeEditable = mode === 'edit'
            const currentlyEditable = crudContext.formContext.isEditable
            
            // Якщо стан редагування не відповідає режиму - перезавантажуємо дані
            if (shouldBeEditable !== currentlyEditable) {
                loadResourceData()
            }
        }
    }, [mode])

    /**
     * Рендерінг шаблону форми
     * Важливо: це має бути компонент, а не useCallback, щоб дозволити використання хуків в кастомних шаблонах
     */
    const RenderFormTemplate = observer(() => {
        if (!crudContext || !crudContext.formContext) return null

        const formContext = crudContext.formContext

        if (formContext.isLoading) return loadingTemplate

        // Визначаємо template з урахуванням поточного mode
        const TemplateComponent = determineTemplate(mode, crudContext.resource)

        if (TemplateComponent) {
            return (
                <TemplateComponent 
                    crudContext={crudContext} 
                    formContext={formContext}
                    mode={mode}
                />
            )
        }

        // Дефолтний рендеринг якщо template не знайдено
        const fields = DeepCloneTask(crudContext.resource?.fields ?? {})

        const fieldElements = Object.keys(fields).map((name) => {
            if (name == 'id') return null
            let field = fields[name]
            return <FormField key={name} name={name} {...field} />
        })

        return <Form formContext={formContext}>{fieldElements}</Form>
    })

    /**
     * Встановлюємо RenderFormTemplate як template у crudContext
     * Це дозволить templateScripts виконуватися через crudContext.render()
     */
    useEffect(() => {
        if (crudContext) {
            crudContext.setTemplate(RenderFormTemplate)
        }
    }, [crudContext, RenderFormTemplate])

    /**
     * Обробник закриття drawer/modal
     */
    const handleClose = () => {
        console.log('[ResourceDetail] handleClose called:', {
            displayMode,
            mode,
            itemId,
            resourceKey: crudContext?.resource?.key,
            hasOnClose: typeof onClose === 'function',
            hasDrawerContext: !!crudContext?.drawerContext,
        })
        // Викликаємо callback користувача
        if (onClose) {
            console.log('[ResourceDetail] Calling onClose callback')
            onClose()
        }

        // Закриваємо через drawerContext якщо є
        if (crudContext?.drawerContext) {
            crudContext.drawerContext.setIsOpen(false)
        }

        // Навігація назад через history stack
        if (typeof window !== 'undefined' && displayMode !== 'page') {
            const resourceName = crudContext?.resource?.key || ''
            console.log('[ResourceDetail] safeGoBack:', { resourceName, displayMode })
            historyStackManager.safeGoBack(resourceName, displayMode)
        }
    }

    /**
     * Зберігаємо onClose callback в crudContext для доступу з event handlers
     */
    useEffect(() => {
        if (crudContext && handleClose) {
            crudContext.closeResourceDetail = handleClose
        }
    }, [crudContext, handleClose])

    /**
     * Рендерінг контенту з відповідним wrapper
     */
    const renderContent = () => {
        const content = (
            <CrudResourceContextProvider value={crudContext}>
                {crudContext.render()}
            </CrudResourceContextProvider>
        )

        // Кастомний блок під контентом detail-форми (page/drawer/modal), напр. службова інформація.
        // Підключається через crudContext.storages.detailFooter (мержиться з CrudResourceConfig,
        // аналогічно storages.tableAddActionsMenuDataSourceItemTransformer для списків).
        const DetailFooter = crudContext?.storages?.detailFooter
        const detailFooter = DetailFooter ? <DetailFooter crudContext={crudContext} mode={mode} /> : null

        // Якщо це звичайна сторінка - рендеримо без wrapper
        if (displayMode === 'page') {
            // Підготовка кнопок для page
            let buttons = {}
            let extraContent = []
            
            // Викликаємо onButtons event для додавання кнопок
            if (crudContext?.eventManager) {
                crudContext.eventManager.emit('onButtons', { buttons, crudContext, content: extraContent })
            }
            
            // Викликаємо користувацький onButtons callback
            // Пріоритет: modeConfig[mode].onButtons > detailConfig.onButtons
            const modeOnButtons = crudContext?.resource?.detailConfig?.modeConfig?.[mode]?.onButtons
            const globalOnButtons = crudContext?.resource?.detailConfig?.onButtons
            
            if (modeOnButtons) {
                modeOnButtons({ buttons, crudContext, content: extraContent })
            } else if (globalOnButtons) {
                globalOnButtons({ buttons, crudContext, content: extraContent })
            }
            
            // Визначаємо title з пріоритетом: detailConfig.title
            const pageTitle = crudContext?.resource?.detailConfig?.title
            
            // Додаємо extra з ButtonGroup якщо є кнопки
            const pageExtra = Object.keys(buttons).length > 0 
                ? [<ButtonGroup key="page-buttons" buttons={buttons} />]
                : (crudContext?.resource?.detailConfig?.extra || [])
            
            // Оновлюємо headerPageContext з crudContext
            if (crudContext?.headerPageContext) {
                if(pageTitle)
                    crudContext.headerPageContext.setTitle(pageTitle)
                crudContext.headerPageContext.extra = pageExtra
            }
            
            return (
                <>
                    <div
                        className="resource-wrapper resource-wrapper--page"
                        data-resource={crudContext?.resource?.key}
                        data-mode={mode}
                        data-display="page"
                        data-level={currentContext?.level ?? 0}
                    >
                        {crudContext?.headerPageContext && (
                            <HeaderPage headerPageContext={crudContext.headerPageContext} />
                        )}
                        {content}
                        {detailFooter}
                    </div>
                    {extraContent}
                </>
            )
        }

        // Drawer режим
        if (displayMode === 'drawer') {
            // Підготовка кнопок для drawer
            let buttons = {}
            let extraContent = []
            
            // Викликаємо onButtons event для додавання кнопок
            if (crudContext?.eventManager) {
                crudContext.eventManager.emit('onButtons', { buttons, crudContext, content: extraContent })
            }
            
            // Викликаємо користувацький onButtons callback
            // Пріоритет: modeConfig[mode].onButtons > detailConfig.onButtons
            const modeOnButtons = crudContext?.resource?.detailConfig?.modeConfig?.[mode]?.onButtons
            const globalOnButtons = crudContext?.resource?.detailConfig?.onButtons
            
            if (modeOnButtons) {
                modeOnButtons({ buttons, crudContext, content: extraContent })
            } else if (globalOnButtons) {
                globalOnButtons({ buttons, crudContext, content: extraContent })
            }
            
            // Визначаємо title з пріоритетом: detailConfig.title > resource.title
            const drawerTitle = crudContext?.resource?.detailConfig?.title || crudContext?.resource?.title || crudContext?.drawerContext?.title
            
            // Додаємо extra з ButtonGroup якщо є кнопки
            const drawerExtra = Object.keys(buttons).length > 0 
                ? [<ButtonGroup key="drawer-buttons" buttons={buttons} />]
                : (crudContext?.resource?.detailConfig?.extra || [])
            
            // Визначаємо width з пріоритетом: modeConfig[mode].width > detailConfig.width > drawer.width
            const drawerWidth = crudContext?.resource?.detailConfig?.modeConfig?.[mode]?.width 
                || crudContext?.resource?.detailConfig?.width 
                || crudContext?.resource?.drawer?.width 
                || crudContext?.drawerContext?.width 
                || 500
            
            // Виключаємо 'width' з конфігу drawer, щоб не передавати застарілий prop до antd Drawer
            const { width: _drawerConfigWidth, ...drawerConfigWithoutWidth } = crudContext?.resource?.drawer || {}
            const { width: _drawerCtxWidth, ...drawerCtxPropsWithoutWidth } = crudContext?.drawerContext?.props || {}

            const drawerProps = {
                ...drawerCtxPropsWithoutWidth,
                ...drawerConfigWithoutWidth,
                open: true,
                onClose: handleClose,
                title: drawerTitle,
                extra: drawerExtra,
                styles: { wrapper: { width: drawerWidth } },
                destroyOnHidden: true,
                maskClosable: true,
                className: 'resource-detail-drawer',
                'data-resource': crudContext?.resource?.key,
                'data-mode': mode,
                'data-display': 'drawer',
                'data-level': currentContext?.level ?? 0,
            }

            return (
                <>
                    <Drawer {...drawerProps}>
                        {content}
                        {detailFooter}
                    </Drawer>
                    {extraContent}
                </>
            )
        }

        // Modal режим
        if (displayMode === 'modal') {
            // Підготовка кнопок для modal
            let buttons = {}
            let extraContent = []
            
            // Викликаємо onButtons event для додавання кнопок
            if (crudContext?.eventManager) {
                crudContext.eventManager.emit('onButtons', { buttons, crudContext, content: extraContent })
            }
            
            // Викликаємо користувацький onButtons callback
            // Пріоритет: modeConfig[mode].onButtons > detailConfig.onButtons
            const modeOnButtons = crudContext?.resource?.detailConfig?.modeConfig?.[mode]?.onButtons
            const globalOnButtons = crudContext?.resource?.detailConfig?.onButtons
            
            if (modeOnButtons) {
                modeOnButtons({ buttons, crudContext, content: extraContent })
            } else if (globalOnButtons) {
                globalOnButtons({ buttons, crudContext, content: extraContent })
            }
            
            // Визначаємо title з пріоритетом: detailConfig.title > resource.title
            const modalTitle = crudContext?.resource?.detailConfig?.title || crudContext?.resource?.title
            
            // Створюємо footer з ButtonGroup якщо є кнопки
            const modalFooter = Object.keys(buttons).length > 0 
                ? <ButtonGroup key="modal-buttons" buttons={buttons} />
                : null
            
            // Визначаємо width з пріоритетом: modeConfig[mode].width > detailConfig.width > drawer.width
            const modalWidth = crudContext?.resource?.detailConfig?.modeConfig?.[mode]?.width 
                || crudContext?.resource?.detailConfig?.width 
                || crudContext?.resource?.drawer?.width 
                || 800
            
            const modalProps = {
                open: true,
                onCancel: handleClose,
                onOk: handleClose,
                title: modalTitle,
                width: modalWidth,
                destroyOnClose: true,
                maskClosable: true,
                className: 'resource-detail-modal',
                footer: modalFooter,
            }

            return (
                <>
                    <Modal {...modalProps}>
                        <div
                            data-resource={crudContext?.resource?.key}
                            data-mode={mode}
                            data-display="modal"
                            data-level={currentContext?.level ?? 0}
                        >
                            {content}
                            {detailFooter}
                        </div>
                    </Modal>
                    {extraContent}
                </>
            )
        }

        return content
    }

    return renderContent()
}
